import React, { useContext, useEffect, useState } from 'react';
import styles from './index.module.scss';
import SwiperCore from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import CardItem from './CardItem';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { DatePicker, Empty } from '@maxtropy/components';
import { Spin } from 'antd';
import ResizeObserver from 'rc-resize-observer';
import { floor, isNil } from 'lodash-es';
import { OverviewItemProps } from '@/api/energyUnitDashboard';
import { EnergyUsageCostContext } from '@/pages/EnergyUsageCostKanban';
import {
  apiV2EnergyUnitBoardComparisonOverviewPost,
  apiV2EnergyUnitPrimaryEnergyUsingUnitPost,
  V2EnergyUnitPrimaryEnergyUsingUnitPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { RangeValue } from '../SwitchRangePicker';
import { EnergyAnalysisOverviewContext } from '../..';

interface UnitSwiperProps {
  setCurrentUnit: ({ unitName, unitId }: { unitName?: string; unitId?: number }) => void;
}

export type EnergyUsingUnitItemProps = Exclude<
  V2EnergyUnitPrimaryEnergyUsingUnitPostResponse['list'],
  undefined
>[number];

export interface UnitItemCostProps extends Omit<EnergyUsingUnitItemProps, 'detailList'> {
  detailList: { id: number; name: string; value: string }[];
}

const UnitSwiper: React.FC<UnitSwiperProps> = ({ setCurrentUnit }) => {
  const { groupId, oneLevelUnits } = useContext(EnergyUsageCostContext);
  const { time, timeResolution, dataQueryPeriodId } = useContext(EnergyAnalysisOverviewContext);
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | undefined>();
  const [unitList, setUnitList] = useState<UnitItemCostProps[]>([]);
  const [currentUnitId, setCurrentUnitId] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isNil(groupId)) return;
    if (!time || !time[0] || !time[1]) return;
    if (oneLevelUnits!.length && !oneLevelUnits!.some(item => item.energyUnitGroupId === groupId)) return;

    setLoading(true);
    apiV2EnergyUnitPrimaryEnergyUsingUnitPost({
      energyUnitGroupId: groupId,
      fromTime: time![0]!.valueOf(),
      toTime: time![1]!.valueOf(),
      timeResolution,
      periodId: dataQueryPeriodId,
    })
      .then(res => {
        const lastUnits = (res.list ?? []).map(unit => {
          let detailList =
            unit.detailList?.map(item => ({
              id: item.energyMediumId,
              name: item.energyMediumName,
              value: item.energyUseCost,
            })) ?? [];

          if (!isNil(unit.periodCost)) {
            detailList.push({
              id: unit.periodCost.periodId! + unit.energyUnitId!,
              name: unit.periodCost.periodName + '-总成本',
              value: unit.periodCost.totalCost,
            });

            (unit.periodCost.detailList ?? []).forEach(item => {
              detailList.push({
                id: unit.periodCost!.periodId! + unit.energyUnitId! + item.energyMediumId!,
                name: `${unit.periodCost?.periodName}-${item.energyMediumName}`,
                value: item.energyUseCost,
              });
            });
          }

          return {
            ...unit,
            detailList,
          };
        });
        setUnitList(lastUnits as UnitItemCostProps[]);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time, dataQueryPeriodId, oneLevelUnits]);

  useEffect(() => {
    if (!unitList.length) {
      setCurrentUnitId(undefined);
    } else {
      setCurrentUnitId(unitList[0].energyUnitId);
    }
  }, [unitList]);

  useEffect(() => {
    if (isNil(currentUnitId)) {
      setCurrentUnit({ unitId: undefined, unitName: undefined });
    } else {
      const currentUnitName = unitList.find(f => f.energyUnitId === currentUnitId)?.energyUnitName;
      setCurrentUnit({ unitId: currentUnitId, unitName: currentUnitName! });
    }
  }, [currentUnitId]);

  return (
    <div className={styles.swiperBox}>
      <Spin spinning={loading}>
        <div className={styles.cardWrapper}>
          {unitList.length ? (
            <div className={unitList.length > 5 ? styles.cardList : ''}>
              <ResizeObserver>
                <Swiper
                  spaceBetween={8}
                  slidesPerView={4}
                  freeMode={true}
                  observer={true}
                  observeParents={true}
                  breakpoints={{
                    0: {
                      slidesPerView: 4, // 小于1600时显示4个
                      navigation: unitList.length > 3,
                    },
                    1600: {
                      slidesPerView: 5, // 大于等于1600时显示5个
                      navigation: unitList.length > 4,
                    },
                  }}
                  onSwiper={swiper => setSwiperInstance(swiper)}
                  navigation={{
                    nextEl: '.swiper-button-prev',
                    prevEl: '.swiper-button-next',
                  }}
                >
                  {unitList.map((item, index) => (
                    <SwiperSlide key={index}>
                      <CardItem
                        overview={item}
                        setSwiperUnitId={id => setCurrentUnitId(id)}
                        swiperUnitId={currentUnitId}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </ResizeObserver>
              {unitList.length > 5 ? (
                <>
                  <div className={`swiper-button-prev`} onClick={() => swiperInstance?.slidePrev()}>
                    <LeftOutlined />
                  </div>
                  <div className={`swiper-button-next`} onClick={() => swiperInstance?.slideNext()}>
                    <RightOutlined />
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <Empty />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default UnitSwiper;
